import { MapTypeId } from "core/features/maps/enums";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import delay from "delay";
import { MapRef, UseMapaParams } from "../typings";
import { DEFAULT_ZOOM } from "../config";
import { CenterMapContext } from "core/features/maps/center-map/CenterMap.context";
import { useFilters } from "core/features/filters/store";
import { TipoMapaTalhoes } from "core/features/maps/typings";

/** Same debounce used by Mapa de Execução Operacional. */
const IDLE_LABELS_DEBOUNCE_MS = 120;

/**
 * Marker visibility strategy aligned with planejamento operacional:
 * - hide labels while pan/zoom (`isInteracting`) without unmounting
 * - restore only after map `idle` (debounced)
 * - require zoom >= 12 before showing labels
 */
export function useMap(params?: UseMapaParams) {
  const mapRef = useRef<MapRef>(null);
  const currentData = useRef<google.maps.Data.Feature[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [zoomAllowsMarkers, setZoomAllowsMarkers] = useState(false);
  const [isInteracting, setIsInteracting] = useState(false);
  const isInteractingRef = useRef(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    filters: { tipoMapa },
  } = useFilters();
  const { shouldForceCenter, handleChangeShouldForceCenter } =
    useContext(CenterMapContext);

  const { onClickItem, points, center } = params ?? {};

  const url = window.location.href;
  const isPrecipitacaoPage = url.includes("precipitacao");

  const beginInteraction = useCallback(() => {
    if (isInteractingRef.current) return;
    isInteractingRef.current = true;
    setIsInteracting(true);
  }, []);

  const syncAfterIdle = useCallback(() => {
    const nextZoom = mapRef.current?.getZoom() || 15;
    setZoomAllowsMarkers((prev) => {
      const next = nextZoom >= 12;
      return prev === next ? prev : next;
    });
    if (isInteractingRef.current) {
      isInteractingRef.current = false;
      setIsInteracting(false);
    }
  }, []);

  const scheduleIdleSync = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    idleTimerRef.current = setTimeout(() => {
      idleTimerRef.current = null;
      syncAfterIdle();
    }, IDLE_LABELS_DEBOUNCE_MS);
  }, [syncAfterIdle]);

  const onLoad = useCallback(
    (map) => {
      const bounds = new google.maps.LatLngBounds();
      map.fitBounds(bounds);
      mapRef.current = map;

      setTimeout(() => setIsLoaded(true), 200);

      const initialZoom = map.getZoom() || 15;
      setZoomAllowsMarkers(initialZoom >= 12);

      map.addListener("zoom_changed", () => {
        beginInteraction();
      });

      map.addListener("dragstart", () => {
        beginInteraction();
      });

      map.addListener("idle", () => {
        scheduleIdleSync();
      });
    },
    [beginInteraction, scheduleIdleSync]
  );

  const onUnmount = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    mapRef.current = null;
  }, []);

  useEffect(() => {
    if (typeof onClickItem !== "function" || !mapRef.current) {
      return;
    }

    const listener = mapRef.current.data.addListener("click", (ev: any) => {
      const prop = Object.keys(ev.feature).find((key) => {
        return !!ev.feature[key]?.id;
      });

      if (prop) {
        onClickItem(ev.latLong, ev.feature[prop]);
      }
    });

    return () => listener?.remove();
  }, [onClickItem]);

  useEffect(() => {
    (async () => {
      if (!points || !isLoaded || !mapRef.current) {
        return;
      }

      currentData.current.forEach((feature) => {
        mapRef.current?.data.remove(feature);
      });

      currentData.current = mapRef.current.data.addGeoJson(points);

      const isChuvasMap = tipoMapa === TipoMapaTalhoes.Chuvas;

      mapRef.current.data.setStyle((feature) => {
        const geometry = feature.getGeometry();
        const geometryType = geometry?.getType?.();
        const isPoint =
          geometryType === "Point" || geometryType === "MultiPoint";

        if (isPoint) {
          return {
            visible: false,
            clickable: false,
          };
        }

        const codigo = feature.getProperty("codigo");
        const isCodigoInTalhoes = params?.talhoes
          ? params?.talhoes!.includes(codigo)
          : false;

        const fill = isChuvasMap
          ? "#000000"
          : !isPrecipitacaoPage
          ? feature.getProperty("fill")
          : isCodigoInTalhoes
          ? "#437BFF"
          : "#00000000";

        const fillOpacity = isChuvasMap
          ? 0
          : feature.getProperty("fillOpacity");
        const stroke = feature.getProperty("stroke") || "#000000";
        const strokeOpacity = feature.getProperty("strokeOpacity") ?? 1;
        const strokeWidth = isChuvasMap
          ? Math.max(Number(feature.getProperty("strokeWidth")) || 1, 1)
          : feature.getProperty("strokeWidth");

        return {
          opacity: fillOpacity,
          fillColor: fill,
          fillOpacity,
          strokeColor: stroke,
          strokeOpacity,
          strokeWidth,
          zIndex: isChuvasMap ? 2 : undefined,
        };
      });
    })();

    return () => {
      mapRef.current?.data.setValues();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, isLoaded, tipoMapa]);

  useEffect(() => {
    (async () => {
      if (
        !center ||
        !isLoaded ||
        !mapRef.current ||
        (!shouldForceCenter &&
          tipoMapa !== TipoMapaTalhoes.ConcentracaoColheita)
      ) {
        return;
      }
      await delay(200);

      mapRef.current?.setCenter({
        lat: center.lat,
        lng: center.long,
      });

      mapRef.current?.setZoom(DEFAULT_ZOOM);
      mapRef.current?.setMapTypeId(MapTypeId.HYBRID);

      syncAfterIdle();
      handleChangeShouldForceCenter(false);
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center, isLoaded, tipoMapa, syncAfterIdle]);

  // Effective flag: hide during gesture, restore on idle (operacional pattern).
  const displayMarkers = zoomAllowsMarkers && !isInteracting;
  const heavyOverlaysEnabled = !isInteracting;

  return {
    isLoaded,
    mapRef,
    onLoad,
    onUnmount,
    displayMarkers,
    zoomAllowsMarkers,
    heavyOverlaysEnabled,
    isInteracting,
  };
}
